import { createAsyncThunk } from '@reduxjs/toolkit';

import { selectIsCallCanBeClosed } from './callFinishSelectors';

import type { RootState } from 'features/app/store/store';
import { LogLevel } from 'features/analytics/enums';
import {
  sendAnalyticsError,
  sendAnalyticsInfo,
} from 'features/analytics/helpers';
import { ConferenceWindowService } from 'features/call/call-conference/services';
import { VoiceMeetingEventBus } from 'features/voice-meeting/services';
import { appUnloading } from 'features/app/store/app/appSlice';
import {
  CallType,
  InterpretingSessionType,
  TerminationType,
} from 'features/call/call-base/enums';
import { CallDaoService } from 'features/call/call-base/services';
import {
  callIdSelector,
  callTypeSelector,
  selectActualCallOwnerUserId,
  terminationTypeSelector,
} from 'features/call/call-base/store/selectors';
import {
  isTwoLineVcoConnectedSelector,
  isPrimaryHearingConnectedSelector,
  primaryHearingSelector,
} from 'features/call/call-hearing/store';
import { SipSessionEventBus } from 'features/sip/services';
import { TeamingDaoService } from 'features/teaming/teaming-base/services';
import {
  isTeamingInProgressSelector,
  isTeamingStartedSelector,
} from 'features/teaming/teaming-base/store';
import { VoiceSessionDaoService } from 'features/voice-session/services';
import { modeSelector } from 'features/multi-mode/store';
import { Mode } from 'features/multi-mode/enums';
import { wasCallPlacedSelector } from 'features/call/vrs-call/store/vrsCallSelectors';
import { isDeafConnectedSelector } from 'features/call/call-deaf/store';
import { sessionIdSelector } from 'features/session/store';
import { selectSignMailStatus } from 'features/signmail/store';
import { voiceSessionIdSelector } from 'features/voice-session/store';
import { isPrimaryVcoSenderSelector } from 'features/vco/store';
import { selectIsViNoLongerCallOwner } from 'features/reporting/store';
import { RtcSessionTerminateCauses } from 'features/sip/enums';

export const leaveCall = createAsyncThunk(
  'call/leaveCall',
  async (
    {
      rtcConnection,
      stopRtcTracksAndCloseConnection,
      handleStoppedTakingCalls,
    }: {
      rtcConnection: any;
      stopRtcTracksAndCloseConnection: (connection: any) => void;
      handleStoppedTakingCalls: () => void;
    },
    { getState, dispatch }
  ) => {
    const state = getState() as RootState;

    // Get all required state values using selectors
    const mode = modeSelector(state);
    const userId = selectActualCallOwnerUserId(state);
    const callId = callIdSelector(state);
    const primaryHearing = primaryHearingSelector(state);
    const isTeamingStarted = isTeamingStartedSelector(state);
    const isTeamingInProgress = isTeamingInProgressSelector(state);
    const voiceSessionId = voiceSessionIdSelector(state);
    const isPrimaryVcoSender = isPrimaryVcoSenderSelector(state);
    const terminationType = terminationTypeSelector(state);
    const isDeafConnected = isDeafConnectedSelector(state);
    const isHearingConnected = isPrimaryHearingConnectedSelector(state);
    const sessionId = sessionIdSelector(state);
    const isTwoLineVcoConnected = isTwoLineVcoConnectedSelector(state);
    const wasCallPlaced = wasCallPlacedSelector(state);
    const signMailStatus = selectSignMailStatus(state);
    const callType = callTypeSelector(state);
    const isConferenceCall = callType === CallType.CONFERENCE_CALL;
    const isViNoLongerCallOwner = selectIsViNoLongerCallOwner(state);
    const { reasonWhyCallDidNotAutoClose } = selectIsCallCanBeClosed(state);

    dispatch(appUnloading());

    if (isTeamingStarted || (isTeamingInProgress && isConferenceCall)) {
      VoiceMeetingEventBus.finishAndDeleteVoiceSession();
      SipSessionEventBus.terminate$.next({
        cause: RtcSessionTerminateCauses.UNEXPECTED_LEAVE_TERMINATION,
      });
      if (rtcConnection) {
        stopRtcTracksAndCloseConnection(rtcConnection);
      }
      await TeamingDaoService.completeTeaming({
        callId,
        userId,
        keepAlive: true,
      });
      // Don't close the call; teammate may still be connected.
      return;
    }

    if (mode === Mode.VRS && isPrimaryVcoSender && !isViNoLongerCallOwner) {
      // Immediately finalize the call to avoid missing it during refresh
      CallDaoService.finalizeUnexpectedEnd({
        callId,
        userId,
        terminationType,
        wasCallPlaced,
        isDeafConnected,
        isHearingConnected,
        interpretingSessionType: InterpretingSessionType.NORMAL,
        sessionId,
        isTwoLineVcoConnected,
        signMailStatus,
      });
    }

    VoiceMeetingEventBus.finishAndDeleteVoiceSession();
    SipSessionEventBus.terminate$.next({
      cause: RtcSessionTerminateCauses.UNEXPECTED_LEAVE_TERMINATION,
    });

    if (rtcConnection) {
      stopRtcTracksAndCloseConnection(rtcConnection);
    }

    try {
      if (voiceSessionId && primaryHearing) {
        await VoiceSessionDaoService.deleteParticipant({
          sessionId: voiceSessionId,
          participantId: primaryHearing.id,
          keepAlive: true,
        });
      }
    } catch (error: any) {
      dispatch(
        sendAnalyticsError({
          Level: LogLevel.ERROR,
          Method: 'leaveCall',
          Message: error?.message,
        })
      );
    }

    if (mode === Mode.VRS) {
      if (!isPrimaryVcoSender || isViNoLongerCallOwner) {
        dispatch(
          sendAnalyticsInfo({
            Level: LogLevel.INFO,
            Method: 'leaveCall',
            Message:
              'VRS call is not primary VCO sender or VI is no longer call owner, skipping call finalization',
            Event: 'MercuryPageRefreshed',
            ReasonCallDidNotAutoClose: reasonWhyCallDidNotAutoClose,
          })
        );
        return;
      }
      dispatch(
        sendAnalyticsInfo({
          Level: LogLevel.INFO,
          Method: 'leaveCall',
          Event: 'MercuryPageRefreshed',
          Message: `VRS call is primary VCO sender and VI is call owner, finalizing call`,
          ReasonCallDidNotAutoClose: reasonWhyCallDidNotAutoClose,
        })
      );
      return;
    }

    await CallDaoService.closeCall({
      callId,
      userId,
      terminationType: TerminationType.NormVRI,
      abandonAfterTaken: false,
      keepAlive: true,
    });
    handleStoppedTakingCalls();
    ConferenceWindowService.close();
  }
);
